/**
 * 3D Room Visualizer — Wall Mesh Builder
 *
 * Converts board layout data into Three.js meshes. Walls are rendered as REAL
 * extruded geometry (W&CB and SlatWall cross-section profiles swept along each
 * board's run), merged per color for performance. The ceiling still uses flat
 * quads.
 */

import * as THREE from 'three';
import { BoardRect, WallBoardLayout, WallFace, CeilingBoardLayout, CeilingPlaneLayout } from './types';
import { outlineTopPieces } from '../../../calculations/sections/outline';
import { buildCeilingBareFill, buildWallBareFill } from './bareFill';
import type { BareFillId } from './renderSettings';
import { createBoardMaterial, createSeamMaterial } from './materialFactory';
import { seamLines, SeamLine } from './seamStrips';
import { buildExtrudedBoard, buildExtrudedBoardGeometry, applyFaceTransformToGroup } from './extrudedBoardBuilder';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { getProfile } from './profiles/profileRegistry';
import { boardCoverage, coverageBoundary, coverageStretches, isVerticalBoard } from './boardCoverage';
import { buildWallTrimGroup, buildCeilingTrimGroup } from './trimMeshBuilder';
import { buildWallOpeningsGroup, tagMovingParts } from './openingMeshBuilder';
import { SLATWALL_COVERAGE_HEIGHT, PANEL_WIDTH_FEET } from '../../../calculations/constants';

// Inset from wall plane to prevent z-fighting (feet)
const WALL_INSET = 0.01;

// Shared W&CB face bump map (vertical striations + edge seam grooves). Served from
// /public so it can be edited and seen on a plain browser refresh (no rebuild).
// Mapped via custom UVs: U = the 16" coverage (ClampToEdge — seam grooves on the
// row joints), V = length (Repeat — striations run along the board).
const WCB_BUMP_URL = `${process.env.PUBLIC_URL || ''}/textures/wcb-bump-map.png`;
const WCB_BUMP_TEXTURE = new THREE.TextureLoader().load(WCB_BUMP_URL, () => {
  // The viewer renders on-demand, so nudge it once the bump map finishes loading.
  WCB_BUMP_TEXTURE.needsUpdate = true;
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('wcb-bump-loaded'));
});
WCB_BUMP_TEXTURE.wrapS = THREE.ClampToEdgeWrapping;
WCB_BUMP_TEXTURE.wrapT = THREE.RepeatWrapping;
WCB_BUMP_TEXTURE.colorSpace = THREE.NoColorSpace; // bump data, not color
const WCB_BUMP_SCALE = 0.5; // visible default; tune to taste

// ============================================================================
// SEAMS
// ============================================================================

/** How far in front of the board faces the seam strips sit (feet, ≈ 0.02"). */
const SEAM_PROUD_FT = 0.02 / 12;

/** Where the visible face of the W&CB profile starts and ends across the board (feet). */
function wcbFaceSpan(): [number, number] {
  const profile = getProfile('WCB');
  const onFace = profile.points.filter(([depth]) => depth >= profile.depthSize * 0.9).map(([, v]) => v);
  return [Math.min(...onFace), Math.max(...onFace)];
}

/**
 * How far past the nominal course line the groove between two courses sits:
 * the middle of the gap between the nearer board's face ending and the further
 * one's beginning. Strips (vertical boards) are the same profile turned on its
 * side and laid the other way round, so the gap falls differently.
 */
function wcbGrooveOffset(vertical: boolean): number {
  const [faceStart, faceEnd] = wcbFaceSpan();
  const full = getProfile('WCB').verticalSize;
  const [from, to] = vertical
    ? [full - faceStart - PANEL_WIDTH_FEET, full - faceEnd]
    : [faceEnd - PANEL_WIDTH_FEET, faceStart];
  return (from + to) / 2;
}

/** How far either side of a seam's centre line its quad reaches (feet, 2"): room for the widest seam plus a pixel. */
const SEAM_REACH_FT = 2 / 12;

/**
 * One mesh for a set of seams: a quad per seam, reaching SEAM_REACH_FT either
 * side of its centre line and carrying the distance from that line (`aAcross`),
 * which the seam material turns into a true-width, evenly drawn line.
 * `place` puts a surface point in 3D. Hidden until the designer's render
 * settings switch seams on.
 */
function buildSeamMesh(seams: SeamLine[], place: (x: number, y: number) => [number, number, number]): THREE.Mesh | null {
  if (seams.length === 0) return null;
  const positions = new Float32Array(seams.length * 12);
  const across = new Float32Array(seams.length * 4);
  const indices: number[] = [];
  seams.forEach((s, i) => {
    const lo = s.at - SEAM_REACH_FT;
    const hi = s.at + SEAM_REACH_FT;
    // Counter-clockwise seen from the room, whichever way the seam runs.
    const corners: [number, number, number][] = s.along === 'x'
      ? [[s.from, lo, -1], [s.to, lo, -1], [s.to, hi, 1], [s.from, hi, 1]]
      : [[lo, s.from, -1], [hi, s.from, 1], [hi, s.to, 1], [lo, s.to, -1]];
    corners.forEach(([x, y, side], v) => {
      positions.set(place(x, y), i * 12 + v * 3);
      across[i * 4 + v] = side * SEAM_REACH_FT;
    });
    const o = i * 4;
    indices.push(o, o + 1, o + 2, o, o + 2, o + 3);
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('aAcross', new THREE.BufferAttribute(across, 1));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  const material = createSeamMaterial();
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'seams';
  mesh.visible = false;
  mesh.userData.skipAO = true; // a tint, not a surface — keep it out of the occlusion pass
  // The wall fade writes `material.opacity`; the shader reads its own uniform.
  mesh.onBeforeRender = () => { material.uniforms.uOpacity.value = material.opacity; };
  return mesh;
}

// ============================================================================
// SHAPED WALLS — clipping to the outline
// ============================================================================

/**
 * The transform from a wall's canonical front-face coordinates to room
 * coordinates — the same mapping `applyFaceTransformToGroup` applies (a
 * reflection for the left and right walls), as a matrix.
 */
export function faceMatrix(face: WallFace, roomLength: number, roomWidth: number): THREE.Matrix4 {
  switch (face) {
    case 'front': return new THREE.Matrix4();
    case 'back': return new THREE.Matrix4().makeRotationY(Math.PI).setPosition(roomLength, 0, roomWidth);
    case 'left': return new THREE.Matrix4().set(0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1);
    case 'right': return new THREE.Matrix4().set(0, 0, -1, roomLength, 0, 1, 0, 0, -1, 0, 0, roomWidth, 0, 0, 0, 1);
  }
}

/**
 * The half-spaces that together are "under the wall's top": one plane per
 * straight piece of the outline's top edge, in canonical wall coordinates,
 * keeping what lies below it. Boards and seams are drawn over the wall's
 * rectangle and cut to the wall with these (material clipping planes), the
 * way the installer cuts them to the rake.
 */
function outlineClipPlanes(outline: [number, number][]): THREE.Plane[] {
  return outlineTopPieces(outline)
    .filter((p) => Math.abs(p.y1 - p.y0) > 1e-6) // a level piece cuts nothing the layout did not already
    .map((p) => {
      // Normal to the edge, pointing down (into the wall).
      const normal = new THREE.Vector3(p.y1 - p.y0, -(p.x1 - p.x0), 0).normalize();
      return new THREE.Plane().setFromNormalAndCoplanarPoint(normal, new THREE.Vector3(p.x0, p.y0, 0));
    });
}

/**
 * Note on every mesh in `group` the planes (room coordinates) it is to be
 * clipped by. Three's clipping planes are in WORLD coordinates, and the room
 * is not at the world origin, so the planes are only put on the materials
 * once the room is in place — see `bakeRoomClipping`.
 */
function noteRoomClip(group: THREE.Object3D, planes: THREE.Plane[]): void {
  group.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) o.userData.roomClip = [...((o.userData.roomClip as THREE.Plane[] | undefined) ?? []), ...planes];
  });
}

/**
 * Put the noted clipping planes on the materials of everything under `root`,
 * in world coordinates: each mesh's planes are those of the nearest ancestor
 * tagged `userData.roomRoot` (the room the mesh is part of), moved by that
 * room's world matrix. Call after the room groups are positioned.
 */
export function bakeRoomClipping(root: THREE.Object3D): void {
  root.updateMatrixWorld(true);
  root.traverse((o) => {
    const planes = o.userData.roomClip as THREE.Plane[] | undefined;
    if (!planes || !(o as THREE.Mesh).isMesh) return;
    // The planes are in room coordinates — or, on a plane of a profiled
    // ceiling, in that plane's own, so its group's matrix takes them to the room.
    let room: THREE.Object3D | null = o;
    while (room && !room.userData.roomRoot && !room.userData.ceilingPlane) room = room.parent;
    const m = (room ?? root).matrixWorld;
    const mesh = o as THREE.Mesh;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      material.clippingPlanes = [...(material.userData.ownClip ?? []), ...planes.map((p) => p.clone().applyMatrix4(m))];
      material.clipShadows = true;
    }
  });
}

// ============================================================================
// WALL MESH BUILDING
// ============================================================================

/**
 * Build a complete wall group for a single wall face. Returns a THREE.Group of
 * extruded board meshes (W&CB merged per color, SlatWall one mesh per row),
 * built in canonical front-face coords then transformed onto the given face.
 */
export function buildWallMesh(
  layout: WallBoardLayout,
  face: WallFace,
  roomLength: number,
  roomWidth: number,
  roomHeight: number,
  /** The ceiling's height over a point of the room (room coordinates), when it is not flat at `roomHeight`. */
  ceilingAt?: (x: number, z: number) => number,
  /** What the wall shows where it is bare (bareFill.ts). Absent: nothing, as before. */
  bareFill?: BareFillId,
): THREE.Group {
  const group = new THREE.Group();
  group.userData = { wallFace: face };

  // Collect all boards from all sections
  const allBoards: BoardRect[] = [];
  for (const section of layout.sections) {
    allBoards.push(...section.boards);
  }

  // A wall with a shaped top (under a sloped ceiling): everything on it is cut
  // to the outline. Planes in room coordinates; applied once the room is placed.
  const clip = layout.outline ? outlineClipPlanes(layout.outline).map((p) => p.applyMatrix4(faceMatrix(face, roomLength, roomWidth))) : [];
  const cutToWall = (g: THREE.Group) => { if (clip.length) noteRoomClip(g, clip); };

  // Edge trim rides in the same group as the boards, so it fades and locks with its wall.
  if (layout.trims && layout.trims.length > 0) {
    const trims = buildWallTrimGroup(layout.trims);
    applyFaceTransformToGroup(trims, face, roomLength, roomWidth);
    group.add(trims);
  }

  // So do the windows and doors.
  if (layout.openings && layout.openings.length > 0) {
    // The ceiling over any point near this wall, in the wall's own coordinates
    // (x along it, z into the room): the room's ceiling where it is known, else
    // flat at the wall's height. A garage door's hardware hangs from it.
    const toRoom = faceMatrix(face, roomLength, roomWidth);
    const over = (x: number, z: number): number => {
      if (!ceilingAt) return Math.min(layout.wallHeight, roomHeight || Infinity);
      const p = new THREE.Vector3(x, 0, z).applyMatrix4(toRoom);
      return ceilingAt(p.x, p.z);
    };
    const openings = buildWallOpeningsGroup(layout.openings, layout.wallLength, over);
    applyFaceTransformToGroup(openings, face, roomLength, roomWidth);
    tagMovingParts(openings, face, roomLength, roomWidth);
    group.add(openings);
  }

  // Where no product is: studs and a sheet, drywall, or a grid — in the wall's
  // group so it fades and locks with the wall, cut to a sloped top like the boards.
  if (bareFill) {
    const bare = buildWallBareFill(layout, bareFill, face, roomLength, roomWidth);
    if (bare) {
      cutToWall(bare);
      group.add(bare);
    }
  }

  if (allBoards.length === 0) return group;

  // Build per SECTION so we know each section's top boundary. The W&CB profile
  // is 17.25" but rows step by 16" — that 1.25" tongue OVERLAP is what makes the
  // boards sit snug (each tongue tucks behind the next row). We keep the full
  // overlap for inner rows and clamp ONLY the top row to the section edge, so it
  // doesn't overshoot (e.g. up behind the SlatWall) without opening gaps.
  for (const section of layout.sections) {
    const boards = section.boards;
    if (boards.length === 0) continue;
    // Tag every group this section emits with its sectionId so the 3D Designer
    // can pick a section by raycasting the mesh directly (robust to the extruded
    // geometry's depth/overlap/grooves). A carved W&CB yields several groups that
    // all share one sectionId — clicking any of them selects the same section.
    const tag = (g: THREE.Group) => { if (section.sectionId) g.userData.sectionId = section.sectionId; };
    const slatBoards = boards.filter(b => b.productType === 'SlatWall');
    const wcb = boards.filter(b => b.productType !== 'SlatWall');
    // A row or a strip: the board says so if it knows (see BoardRect.vertical),
    // otherwise by shape — wider than tall is a row, taller than wide a strip.
    const wcbHoriz = wcb.filter(b => !isVerticalBoard(b));
    const wcbVert = wcb.filter(b => isVerticalBoard(b));

    if (wcbHoriz.length > 0) {
      const g = buildExtrudedWcbBoards(wcbHoriz, false);
      tag(g);
      placePieceClips(g, face, roomLength, roomWidth);
      cutToWall(g);
      applyFaceTransformToGroup(g, face, roomLength, roomWidth);
      group.add(g);
    }
    if (wcbVert.length > 0) {
      const g = buildExtrudedWcbBoards(wcbVert, true);
      tag(g);
      placePieceClips(g, face, roomLength, roomWidth);
      cutToWall(g);
      applyFaceTransformToGroup(g, face, roomLength, roomWidth);
      group.add(g);
    }

    if (slatBoards.length > 0) {
      const slatGroup = buildExtrudedSlatWallSection(slatBoards);
      tag(slatGroup);
      cutToWall(slatGroup);
      applyFaceTransformToGroup(slatGroup, face, roomLength, roomWidth);
      group.add(slatGroup);
    }
  }

  return group;
}

/**
 * Build a Group containing one extruded SlatWall mesh per panel row,
 * positioned in canonical front-face wall coords (the caller applies
 * the per-face transform afterward).
 */
function buildExtrudedSlatWallSection(slatWallBoards: BoardRect[]): THREE.Group {
  const root = new THREE.Group();
  root.name = 'slatwall-extruded';
  const profile = getProfile('SlatWall');

  for (const board of slatWallBoards) {
    const isWhite = board.color.toUpperCase() === '#FFFFFF';
    const materialColor = isWhite ? 'White' : 'Gray';
    // Each panel row is one extruded board. Profile is 7.23" tall (6" exposed
    // face + ~0.5" lip at the BOTTOM that overlaps the section below (W&CB)
    // + ~0.73" at the TOP where the install trim channel clips in flush).
    // Rows step by SLATWALL_COVERAGE_HEIGHT (6").
    //
    // Shift each panel's mesh DOWN by SLATWALL_LIP_HEIGHT so the lip protrudes
    // BELOW the section's nominal bottom (section.y) — onto W&CB below if it's
    // there, matching the real install where the SlatWall covers the top ~0.5"
    // of the W&CB. The lowest panel's lip lands at [section.y - lip, section.y];
    // the topmost panel's profile top lands at [section.top, section.top + 0.73]
    // — where the trim channel sits flush in real installs.
    const SLATWALL_LIP_HEIGHT = 0.5 / 12; // 0.5" in feet

    // A strip cut from a row (over a door, under a sill): the row in its usual
    // place, with everything above and below the strip clipped away. The planes
    // are horizontal, and no wall's transform changes heights, so they hold on
    // every face. Requires renderer.localClippingEnabled = true.
    if (board.rowY !== undefined) {
      const material = createBoardMaterial(materialColor);
      material.clippingPlanes = [
        new THREE.Plane(new THREE.Vector3(0, 1, 0), -board.y),
        new THREE.Plane(new THREE.Vector3(0, -1, 0), board.y + board.height),
      ];
      material.userData.ownClip = material.clippingPlanes; // kept when the wall's own clipping is added (bakeRoomClipping)
      material.clipShadows = false;
      const mesh = buildExtrudedBoard(profile, board.width, material);
      mesh.geometry.translate(board.x, board.rowY - SLATWALL_LIP_HEIGHT, 0);
      root.add(mesh);
      continue;
    }

    const numRows = Math.max(1, Math.round(board.height / SLATWALL_COVERAGE_HEIGHT));
    // y of the section's nominal top — used to clip the topmost panel's tongue
    // (the ~0.73" above this y would in reality be hidden by an install trim
    // channel + cap; here we just clip it so it doesn't overlap any W&CB sitting
    // directly above the SlatWall section).
    const sectionTopY = board.y + numRows * SLATWALL_COVERAGE_HEIGHT;
    for (let row = 0; row < numRows; row++) {
      // Fresh material per panel so opacity/state can later be per-panel.
      const material = createBoardMaterial(materialColor);
      if (row === numRows - 1) {
        // Clip the topmost panel at the section's nominal top — hides the
        // tongue + matches the real-world trim-cap behaviour described by
        // the user. Requires renderer.localClippingEnabled = true.
        material.clippingPlanes = [new THREE.Plane(new THREE.Vector3(0, -1, 0), sectionTopY)];
        material.userData.ownClip = material.clippingPlanes;
        material.clipShadows = false;
      }
      const mesh = buildExtrudedBoard(profile, board.width, material);
      // Placed by moving the geometry, not the mesh: the left / right walls are
      // put in place by rewriting vertices (applyFaceTransformToGroup), which
      // leaves a mesh's own position behind — a panel that starts part-way
      // along the wall (beside a door) would end up outside the room.
      mesh.geometry.translate(
        board.x,
        board.y + row * SLATWALL_COVERAGE_HEIGHT - SLATWALL_LIP_HEIGHT,
        0,
      );
      root.add(mesh);
    }
  }

  return root;
}

/**
 * Build a Group of extruded W&CB planks, MERGED into one geometry per color for
 * performance. Boards are in canonical front-face coords (the caller applies the
 * per-face transform). Handles BOTH orientations:
 *   - horizontal (vertical=false): run = board.width, coverage steps up in Y;
 *   - vertical   (vertical=true):  run = board.height, coverage steps across X
 *     (the extruded plank is rotated 90° so striations run vertically).
 * The 17.25" profile (vertical extent) overlaps the next row's 16" coverage like
 * real T&G; a board with nothing directly beyond it is cut back to its own
 * depth (see boardCoverage.ts).
 */
/**
 * Custom UVs for an extruded board (local X = run, Y = coverage): U is
 * coverage / 16" so the edge seam grooves land on the joints; V is run / 16"
 * so the striations tile along the length.
 */
function setBoardUvs(g: THREE.BufferGeometry): void {
  const pos = g.attributes.position as THREE.BufferAttribute;
  const uvArr = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uvArr[i * 2] = pos.getY(i) / PANEL_WIDTH_FEET;
    uvArr[i * 2 + 1] = pos.getX(i) / PANEL_WIDTH_FEET;
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uvArr, 2));
}

/**
 * The clipped pieces of notched boards in `g` (see buildExtrudedWcbBoards)
 * carry their clipping planes in canonical wall coordinates: put them into
 * room coordinates for this face, to be made world planes with the room
 * (bakeRoomClipping).
 */
function placePieceClips(g: THREE.Group, face: WallFace, roomLength: number, roomWidth: number): void {
  const m = faceMatrix(face, roomLength, roomWidth);
  g.traverse((o) => {
    const planes = o.userData.pieceClip as THREE.Plane[] | undefined;
    if (!planes) return;
    noteRoomClip(o, planes.map((p) => p.clone().applyMatrix4(m)));
    delete o.userData.pieceClip;
  });
}

function buildExtrudedWcbBoards(boards: BoardRect[], vertical: boolean): THREE.Group {
  const root = new THREE.Group();
  root.name = vertical ? 'wcb-extruded-v' : 'wcb-extruded-h';
  const profile = getProfile('WCB');
  const fullV = profile.verticalSize; // 1.4375' (17.25")

  const byColor = new Map<string, BoardRect[]>();
  for (const b of boards) {
    if (!byColor.has(b.color)) byColor.set(b.color, []);
    byColor.get(b.color)!.push(b);
  }

  for (const [colorHex, cboards] of byColor) {
    const geoms: THREE.BufferGeometry[] = [];
    for (const b of cboards) {
      const run = vertical ? b.height : b.width;       // extrusion length
      const covStart = vertical ? b.x : b.y;           // start in the coverage axis
      const across = vertical ? b.width : b.height;    // the piece's own depth
      if (run <= 0) continue;

      // Where the board's course starts and how deep it is: a notched board's
      // pieces are drawn as their course's profile, clipped (below).
      const origin = b.courseStart ?? covStart;
      const courseDepth = b.courseSize ?? across;
      const notched = across < PANEL_WIDTH_FEET - 1e-3;
      // Does the piece still reach its course's far edge — cut back only on the
      // near side (beside a window or door, past a SlatWall band)? Then that
      // edge is an ordinary joint with the next course, and is drawn like one.
      const toCourseEnd = covStart + across >= origin + courseDepth - 1e-3;

      // A piece of a notched board cut short of its course's far edge (the
      // strip left before a SlatWall band): the course's WHOLE profile, at the
      // course's own origin, clipped to the piece — so its ribs and edges are
      // exactly those of the board's full-depth pieces either side of the
      // notch, and the cut shows no line. (Scaling the profile down to the
      // piece's depth did, one course tall, at the edge of every notch.)
      if (b.courseStart !== undefined && notched && !toCourseEnd) {
        const g = buildExtrudedBoardGeometry(profile, run);
        setBoardUvs(g);
        if (vertical) {
          g.rotateZ(Math.PI / 2);
          g.translate(b.courseStart + fullV, b.y, 0);
        } else {
          g.translate(b.x, b.courseStart, 0);
        }
        g.computeVertexNormals();
        const material = createBoardMaterial(colorHex.toUpperCase() === '#FFFFFF' ? 'White' : 'Gray');
        material.bumpMap = WCB_BUMP_TEXTURE;
        material.bumpScale = WCB_BUMP_SCALE;
        const mesh = new THREE.Mesh(g, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        // Keep only the piece's own extent across the boards (canonical wall
        // coordinates; put into place with the wall — see buildWallMesh).
        const axis = vertical ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
        mesh.userData.pieceClip = [
          new THREE.Plane(axis.clone(), -covStart),
          new THREE.Plane(axis.clone().negate(), covStart + across),
        ];
        root.add(mesh);
        continue;
      }

      // A full-width board, or a piece that still reaches its course's far
      // edge: drawn stretch by stretch along its length — the full profile
      // where the next course covers its tongue, cut back to the course's edge
      // where nothing does (a gable's stepped neighbour, a window, the last
      // course), never past the wall's end. Cut back by CLIPPING the full
      // profile, so its face keeps its width: squashing the profile to the
      // board's depth (as it once was) narrowed the face and opened a 1.3" gap
      // at every joint wherever a neighbour stepped — every board of a gable.
      // A piece cut on its near side keeps that cut too. (Clipping such a
      // piece at the course line instead, tongue and all, left an 8 mm gap at
      // its joint with the next course — a line up both sides of every window
      // and SlatWall band, Rob 2026-10-08.)
      if (!notched || b.courseStart !== undefined) {
        const boundary = coverageBoundary(boards, vertical);
        const along0 = vertical ? b.y : b.x;
        const cut = covStart > origin + 1e-6;
        for (const s of coverageStretches(b, boards, vertical)) {
          const depth = Math.min(s.covered ? fullV : courseDepth, boundary - origin);
          if (depth <= 0 || s.to - s.from <= 1e-6) continue;
          const g = buildExtrudedBoardGeometry(profile, s.to - s.from); // local: X=run, Y=0..fullV, Z=depth
          g.translate(s.from - along0, 0, 0); // its place along the board, before the UVs: the striations run on
          setBoardUvs(g);
          if (vertical) {
            g.rotateZ(Math.PI / 2);
            g.translate(origin + fullV, b.y, 0);
          } else {
            g.translate(b.x, origin, 0);
          }
          g.computeVertexNormals();
          if (depth >= fullV - 1e-6 && !cut) { geoms.push(g); continue; }
          // Cut back: the whole profile, clipped (as a notch piece is).
          const material = createBoardMaterial(colorHex.toUpperCase() === '#FFFFFF' ? 'White' : 'Gray');
          material.bumpMap = WCB_BUMP_TEXTURE;
          material.bumpScale = WCB_BUMP_SCALE;
          const mesh = new THREE.Mesh(g, material);
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          const axis = vertical ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
          mesh.userData.pieceClip = [
            new THREE.Plane(axis.clone(), -covStart),
            new THREE.Plane(axis.clone().negate(), origin + depth),
          ];
          root.add(mesh);
        }
        continue;
      }

      // A narrow piece with no course to keep (a sliver): drawn at its own depth.
      const cov = boardCoverage(b, boards, vertical, fullV);
      if (cov <= 0) continue;

      const g = buildExtrudedBoardGeometry(profile, run); // local: X=run, Y=cov(0..fullV), Z=depth
      if (Math.abs(cov - fullV) > 1e-6) g.scale(1, cov / fullV, 1);

      setBoardUvs(g);

      if (vertical) {
        // Map run → wall-Y and coverage → wall-X: rotateZ(90°) sends (x,y)→(-y,x).
        g.rotateZ(Math.PI / 2);
        g.translate(b.x + cov, b.y, 0);
      } else {
        g.translate(b.x, b.y, 0);
      }
      g.computeVertexNormals();
      geoms.push(g);
    }
    if (geoms.length === 0) continue;

    const merged = mergeGeometries(geoms, false);
    geoms.forEach((g) => g.dispose());
    if (!merged) continue;

    const isWhite = colorHex.toUpperCase() === '#FFFFFF';
    const material = createBoardMaterial(isWhite ? 'White' : 'Gray');
    material.bumpMap = WCB_BUMP_TEXTURE;
    material.bumpScale = WCB_BUMP_SCALE;
    const mesh = new THREE.Mesh(merged, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    root.add(mesh);
  }

  // The joints between these boards, just in front of their faces.
  const z = profile.depthSize + SEAM_PROUD_FT;
  const seams = buildSeamMesh(seamLines(boards, { vertical, grooveOffset: wcbGrooveOffset(vertical) }), (x, y) => [x, y, z]);
  if (seams) root.add(seams);

  return root;
}

/**
 * One plane of a profiled ceiling: its boards, seams and trim built exactly
 * as a wall's are (real extruded board, in the plane's own surface
 * coordinates), then placed by the plane's matrix — surface x along the
 * ridge, y up the slope, the boards' faces toward the room.
 */
export function buildCeilingPlaneMesh(plane: CeilingPlaneLayout, roomLength: number, roomWidth: number, bareFill?: BareFillId): THREE.Group {
  const group = buildWallMesh(plane.layout, 'front', roomLength, roomWidth, plane.plane.height, undefined, bareFill);
  group.userData = { ...group.userData, wallFace: plane.id, ceilingPlane: plane.id };
  group.matrix.fromArray(plane.plane.matrix);
  group.matrixAutoUpdate = false;
  return group;
}

/**
 * A profiled ceiling: every plane, in one group (so it fades and locks as
 * "the ceiling"), each plane's group tagged `ceilingPlane` with its id.
 */
export function buildCeilingPlanesMesh(planes: CeilingPlaneLayout[], roomLength: number, roomWidth: number, bareFill?: BareFillId): THREE.Group {
  const group = new THREE.Group();
  group.userData = { wallFace: 'ceiling' };
  for (const plane of planes) group.add(buildCeilingPlaneMesh(plane, roomLength, roomWidth, bareFill));
  return group;
}

/**
 * Build a ceiling group with board meshes.
 */
export function buildCeilingMesh(
  layout: CeilingBoardLayout,
  roomLength: number,
  roomWidth: number,
  roomHeight: number,
  /** What the ceiling shows where it is bare (bareFill.ts). Absent: nothing, as before. */
  bareFill?: BareFillId,
): THREE.Group {
  const group = new THREE.Group();
  group.userData = { wallFace: 'ceiling' };
  // Tag with the originating sectionId so the 3D Designer can click-to-select the
  // ceiling's W&CB section (it's a single full section; meshes merge per color).
  if (layout.sectionId) group.userData.sectionId = layout.sectionId;

  if (layout.trims && layout.trims.length > 0) {
    group.add(buildCeilingTrimGroup(layout.trims, roomHeight - WALL_INSET));
  }

  if (bareFill) {
    const bare = buildCeilingBareFill(layout, bareFill, roomHeight);
    if (bare) group.add(bare);
  }

  if (layout.boards.length === 0) return group;

  // Group boards by color
  const boardsByColor = new Map<string, BoardRect[]>();
  for (const board of layout.boards) {
    if (!boardsByColor.has(board.color)) {
      boardsByColor.set(board.color, []);
    }
    boardsByColor.get(board.color)!.push(board);
  }

  // Create merged mesh per color
  for (const [colorHex, boards] of boardsByColor) {
    const isWhite = colorHex.toUpperCase() === '#FFFFFF';
    const material = createBoardMaterial(isWhite ? 'White' : 'Gray');

    const mesh = createMergedCeilingBoardMesh(boards, roomLength, roomWidth, roomHeight, material);
    group.add(mesh);
  }

  // Add seam lines to ceiling boards
  const hairlines = createCeilingSeamLines(layout.boards, roomLength, roomHeight);
  if (hairlines) {
    // The designer's render settings draw true-width seams instead (below) and hide these.
    hairlines.userData.legacySeams = true;
    group.add(hairlines);
  }

  // Seams, as on the walls: just below the boards, facing down into the room.
  const seamY = roomHeight - WALL_INSET - SEAM_PROUD_FT;
  const lines = [false, true].flatMap((vertical) =>
    seamLines(layout.boards.filter((b) => isVerticalBoard(b) === vertical), { vertical }));
  const seams = buildSeamMesh(lines, (x, y) => [x, seamY, y]);
  if (seams) group.add(seams);

  return group;
}

// ============================================================================
// INTERNAL HELPERS (ceiling — still flat quads)
// ============================================================================

/**
 * Create merged board mesh for ceiling boards.
 * Boards are in XZ plane at Y=roomHeight.
 */
function createMergedCeilingBoardMesh(
  boards: BoardRect[],
  roomLength: number,
  _roomWidth: number,
  roomHeight: number,
  material: THREE.MeshStandardMaterial
): THREE.Mesh {
  const numBoards = boards.length;
  const positions = new Float32Array(numBoards * 4 * 3);
  const normals = new Float32Array(numBoards * 4 * 3);
  const indices: number[] = [];

  // Ceiling normal faces down (into room)
  const normal = new THREE.Vector3(0, -1, 0);

  for (let i = 0; i < numBoards; i++) {
    const board = boards[i];
    const offset = i * 4 * 3;
    const vertOffset = i * 4;

    // Map board 2D (x=along length, y=along width) to 3D (X, roomHeight+inset, Z)
    const yPos = roomHeight - WALL_INSET;
    const x1 = board.x;
    const x2 = board.x + board.width;
    const z1 = board.y;
    const z2 = board.y + board.height;

    // BL, BR, TR, TL in XZ plane
    positions[offset] = x1;     positions[offset + 1] = yPos; positions[offset + 2] = z1;
    positions[offset + 3] = x2; positions[offset + 4] = yPos; positions[offset + 5] = z1;
    positions[offset + 6] = x2; positions[offset + 7] = yPos; positions[offset + 8] = z2;
    positions[offset + 9] = x1; positions[offset + 10] = yPos; positions[offset + 11] = z2;

    for (let v = 0; v < 4; v++) {
      normals[offset + v * 3] = normal.x;
      normals[offset + v * 3 + 1] = normal.y;
      normals[offset + v * 3 + 2] = normal.z;
    }

    indices.push(
      vertOffset, vertOffset + 1, vertOffset + 2,
      vertOffset, vertOffset + 2, vertOffset + 3
    );
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  return new THREE.Mesh(geometry, material);
}

/**
 * Create seam lines for ceiling boards (XZ plane at Y=roomHeight).
 * Matches the wall seam line style.
 */
function createCeilingSeamLines(
  boards: BoardRect[],
  roomLength: number,
  roomHeight: number
): THREE.LineSegments | null {
  if (boards.length === 0) return null;

  const points: number[] = [];
  const yPos = roomHeight - WALL_INSET + 0.001;

  for (const board of boards) {
    const x1 = board.x;
    const x2 = board.x + board.width;
    const z1 = board.y;
    const z2 = board.y + board.height;

    // All 4 edges
    points.push(x1, yPos, z1, x2, yPos, z1); // front
    points.push(x2, yPos, z1, x2, yPos, z2); // right
    points.push(x2, yPos, z2, x1, yPos, z2); // back
    points.push(x1, yPos, z2, x1, yPos, z1); // left
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));

  const material = new THREE.LineBasicMaterial({
    color: 0x666666,
    linewidth: 1,
    transparent: true,
    opacity: 0.5,
  });

  return new THREE.LineSegments(geometry, material);
}
